import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, DatePickerField, Stepper, Text, TimeField, UseFormReturnType } from '@frontend/design-system';
import { getLabelWithTimezoneAbbr } from '../../../helpers';
import { QUICK_FILL_TRACKING_IDS } from '../../../tracking-ids';
import { SendMessagesFormConfig } from '.';

const trackingIds = QUICK_FILL_TRACKING_IDS.sendMessagesTab;
const MAX_SLOTS = 3;

type TimeFieldKey = 'time1' | 'time2' | 'time3';
type DateFieldKey = 'date1' | 'date2' | 'date3';

type Props = {
  form: UseFormReturnType<SendMessagesFormConfig, keyof SendMessagesFormConfig>;
  timezoneAbbr: string;
  appointmentSlotCount: number;
  setAppointmentSlotCount: React.Dispatch<React.SetStateAction<number>>;
};

export const DateTimeContent = ({ form, timezoneAbbr, appointmentSlotCount, setAppointmentSlotCount }: Props) => {
  const { t } = useTranslation('schedule-quick-fill');

  const handleAddSlot = () => {
    if (appointmentSlotCount < MAX_SLOTS) {
      setAppointmentSlotCount((prev) => prev + 1);
    }
  };

  const handleRemoveSlot = (indexToRemove: number) => {
    // Shift the values up starting from indexToRemove
    for (let i = indexToRemove; i < appointmentSlotCount - 1; i++) {
      const dateFieldKey = `date${i + 2}` as keyof SendMessagesFormConfig;
      const timeFieldKey = `time${i + 2}` as keyof SendMessagesFormConfig;
      form.seedValues({ [`date${i + 1}`]: form.values[dateFieldKey] });
      form.seedValues({ [`time${i + 1}`]: form.values[timeFieldKey] });
    }

    // Clear the last slot
    form.seedValues({ [`date${appointmentSlotCount}`]: '' });
    form.seedValues({ [`time${appointmentSlotCount}`]: '' });

    setAppointmentSlotCount((prev) => prev - 1);
  };

  const hasDateTimesFilled = useCallback(() => {
    let datesValid = true;
    let timesValid = true;
    for (let i = 1; i <= appointmentSlotCount; i++) {
      const dateFieldKey = `date${i}` as keyof SendMessagesFormConfig;
      const timeFieldKey = `time${i}` as keyof SendMessagesFormConfig;
      if (!form.values[dateFieldKey]) {
        datesValid = false;
      }
      if (!form.values[timeFieldKey]) {
        timesValid = false;
      }
    }
    return [datesValid, timesValid];
  }, [form.values, appointmentSlotCount]);

  const getDateTimeHelperText = useCallback(() => {
    const [isValidDate, isValidTime] = hasDateTimesFilled();

    if (!isValidDate && !isValidTime) {
      return t('Enter Date & Time');
    }
    if (!isValidDate) {
      return t('Enter Date');
    }
    if (!isValidTime) {
      return t('Enter Time');
    }
    return '';
  }, [hasDateTimesFilled, t]);

  const hasValidSlots = useCallback(() => {
    for (let i = 1; i <= appointmentSlotCount; i++) {
      const dateFieldKey = `date${i}` as keyof SendMessagesFormConfig;
      const timeFieldKey = `time${i}` as keyof SendMessagesFormConfig;
      if (!form.values[dateFieldKey] || !form.values[timeFieldKey]) return false;
    }
    return true;
  }, [form.values, appointmentSlotCount]);

  const helperText = useMemo(() => getDateTimeHelperText(), [getDateTimeHelperText]);
  const isValid = useMemo(() => hasValidSlots(), [hasValidSlots]);

  return (
    <>
      <div css={styles.container}>
        {[...Array(appointmentSlotCount)].map((_, index) => {
          const number = index + 1;
          const dateFieldKey = `date${number}` as DateFieldKey;
          const timeFieldKey = `time${number}` as TimeFieldKey;
          const dateTrackingId = `dateField${number}` as keyof typeof trackingIds.dateTimeSelectionStep;
          const timeTrackingId = `timeField${number}` as keyof typeof trackingIds.dateTimeSelectionStep;

          return (
            <div key={index} css={styles.card}>
              <div css={styles.cardContainer}>
                <Text weight='bold'>{`${t('Appointment Slot #')}${number}`}</Text>
                {index > 0 && (
                  <Button
                    variant='tertiary'
                    iconName='remove2'
                    destructive
                    css={{ padding: 0 }}
                    onClick={() => handleRemoveSlot(index)}
                  >
                    {t('Remove')}
                  </Button>
                )}
              </div>
              <div css={styles.inputContainer}>
                <DatePickerField
                  {...form.getFieldProps(dateFieldKey)}
                  label={getLabelWithTimezoneAbbr(t('Date'), timezoneAbbr)}
                  css={{ paddingLeft: theme.spacing(1), maxWidth: '100%' }}
                  data-trackingid={trackingIds.dateTimeSelectionStep[dateTrackingId]}
                />
                <TimeField
                  {...form.getFieldProps(timeFieldKey)}
                  showAdornment
                  label={getLabelWithTimezoneAbbr(t('Time'), timezoneAbbr)}
                  data-trackingid={trackingIds.dateTimeSelectionStep[timeTrackingId]}
                />
              </div>
            </div>
          );
        })}
        {appointmentSlotCount < MAX_SLOTS && (
          <Button
            iconName='add'
            variant='tertiary'
            disabled={!isValid}
            css={{ padding: theme.spacing(1, 0.5) }}
            onClick={handleAddSlot}
          >
            {t('Add Slot')}
          </Button>
        )}
      </div>
      <Stepper.ButtonBar>
        <Text size='small' color='subdued'>
          {helperText}
        </Text>
        <Stepper.NextButton trackingId={trackingIds.dateTimeSelectionStep.nextButton} isValid={isValid}>
          {t('Next')}
        </Stepper.NextButton>
      </Stepper.ButtonBar>
    </>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(2)};
  `,
  card: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral[20]};
    width: 100%;
  `,
  cardContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    @media (max-width: ${breakpoints.xsmall.max}px) {
      flex-direction: column;
      align-items: flex-start;
    }
  `,
  inputContainer: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
    @media (max-width: ${breakpoints.xsmall.max}px) {
      flex-direction: column;
    }
  `,
};
